/**
 * FILE: src/blueprint/app/BlueprintRouter.jsx
 * GOAL: Route between Entry, Overview, and Module screens for the MVP runner.
 * FLOW:
 * - Reads current route from useBlueprintRouter() (ROUTES: ENTRY/OVERVIEW/MODULE).
 * - Uses useSessionStatus() to derive entry mode (first/resume/expired).
 * - Renders:
 *   - EntryScreen (default) with primary action -> goToOverview()
 *   - BlueprintOverviewScreen when route === OVERVIEW
 *   - ModuleRunnerScreen when route === MODULE
 *
 * AUTOSTART:
 * - When opened on /run/?pack=... and the packId is resolved in state,
 *   automatically skip Entry and go straight to Overview (module list).
 *
 * @author GoViral Web
 */

import { useEffect, useMemo, useRef } from 'react';
import EntryScreen from '../screens/Entry/EntryScreen.jsx';
import ModuleRunnerScreen from '../screens/Module/ModuleRunnerScreen.jsx';
import BlueprintOverviewScreen from '../screens/Overview/BlueprintOverviewScreen.jsx';
import { useBlueprintRouter } from '../state/useBlueprintRouter.js';
import { useBlueprintState } from '../state/useBlueprintState.js';
import { useSessionStatus } from '../state/useSessionStatus.js';
import { ROUTES } from './routes.js';

function asString(v) {
  return v == null ? '' : String(v);
}

function getPackFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('pack');
    return v ? String(v).trim() : '';
  } catch {
    return '';
  }
}

function getFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('from');
    return v ? decodeURIComponent(String(v)) : '';
  } catch {
    return '';
  }
}

function isRunHostPage() {
  try {
    const path = String(window.location.pathname || '').toLowerCase();
    return path === '/run' || path.startsWith('/run/');
  } catch {
    return false;
  }
}

export default function BlueprintRouter() {
  const session = useSessionStatus();
  const { route, goToOverview } = useBlueprintRouter();
  const { state } = useBlueprintState();

  const didAutostartRef = useRef(false);

  const entryMode = useMemo(() => {
    if (session?.mode === 'expired') return 'expired';
    if (session?.mode === 'resume') return 'resume';
    return 'first';
  }, [session?.mode]);

  const fromUrl = useMemo(() => getFromUrl(), []);

  // AUTOSTART: /run/?pack=... should jump straight to OVERVIEW once packId is ready.
  useEffect(() => {
    if (didAutostartRef.current) return;
    if (!isRunHostPage()) return;

    const urlPack = getPackFromUrl();
    if (!urlPack) return;

    const currentPack = asString(state?.packId).trim();
    if (!currentPack) return;

    if (currentPack.toLowerCase() !== urlPack.toLowerCase()) return;

    // Only auto-skip Entry (never force navigation once user is past Entry)
    if (route !== ROUTES.ENTRY) return;

    // Respect session modes: do not auto-skip on expired (needs explicit user action)
    if (entryMode === 'expired') return;

    didAutostartRef.current = true;
    goToOverview();
  }, [route, entryMode, state?.packId, goToOverview]);

  if (route === ROUTES.MODULE) {
    return <ModuleRunnerScreen fromUrl={fromUrl} />;
  }

  if (route === ROUTES.OVERVIEW) {
    return <BlueprintOverviewScreen fromUrl={fromUrl} />;
  }

  const showDescription = false;
  return (
    <EntryScreen
      mode={entryMode}
      onPrimaryAction={goToOverview}
      packId={state?.packId}
      showDescription={showDescription}
    />
  );
}
